import React, { useState, useEffect, useCallback } from 'react';
import { useGameStore } from '../../store/gameStore';
import { useTranslation } from '../../hooks/useTranslation';
import { BuildingModal } from './BuildingModal';
import { WEAPONS, Weapon } from '../../types/weapon';

const getWeaponSprite = (weapon: Weapon) => `./sprites/weapons/${weapon.name}.png`;

interface ForgeModalProps {
  onClose: () => void;
}

type Category = 'melee' | 'shields' | 'armor' | 'ranged';

export const ForgeModal: React.FC<ForgeModalProps> = ({ onClose }) => {
  const t = useTranslation();
  const { buildings, gold, resources, spendGold, spendResource, buildBuilding, addWeaponToInventory, weaponInventory, fighters } = useGameStore();
  const [activeCategory, setActiveCategory] = useState<Category>('melee');
  const [hoveredWeapon, setHoveredWeapon] = useState<Weapon | null>(null);
  
  // Фильтрация оружия по категориям
  const getWeaponsByCategory = useCallback((category: Category): Weapon[] => {
    const allWeapons = WEAPONS.filter(w => w.id < 10);
    switch (category) {
      case 'melee':
        return allWeapons.filter(w => w.attack > 0 && w.range <= 2 && !w.name.includes('bow'));
      case 'shields':
        const shields = allWeapons.filter(w => w.name.includes('shield'));
        // Деревянный щит (id: 7) в начало списка
        const woodenShield = shields.find(w => w.id === 7);
        const otherShields = shields.filter(w => w.id !== 7);
        return woodenShield ? [woodenShield, ...otherShields] : shields;
      case 'armor':
        return allWeapons.filter(w => w.name.includes('armor'));
      case 'ranged':
        return allWeapons.filter(w => w.name.includes('bow'));
      default:
        return [];
    }
  }, []);

  // Устанавливаем первый предмет категории по умолчанию при смене категории
  useEffect(() => {
    if (buildings.forge) {
      const categoryWeapons = getWeaponsByCategory(activeCategory);
      if (categoryWeapons.length > 0) {
        setHoveredWeapon(categoryWeapons[0]);
      } else {
        setHoveredWeapon(null);
      }
    }
  }, [activeCategory, buildings.forge, getWeaponsByCategory]);

  const maxFighterRank = fighters.length > 0 ? Math.max(...fighters.map(f => f.rank)) : 0;

  const getRankNeeded = (weaponId: number): number => {
    if ([0, 7].includes(weaponId)) return 0;
    if ([1, 3, 5, 8].includes(weaponId)) return 1;
    return 2;
  };

  const handleBuy = (weaponId: number) => {
    const weapon = WEAPONS.find(w => w.id === weaponId);
    if (!weapon) return;
    
    const costGold = weapon.cost || 0;
    const costMetal = weapon.costMetal || 0;
    const costWood = weapon.costWood || 0;
    const costLeather = weapon.costLeather || 0;
    
    if (gold < costGold) return;
    if (resources.metal < costMetal) return;
    if (resources.wood < costWood) return;
    if (resources.leather < costLeather) return;
    
    if (spendGold(costGold)) {
      if (costMetal > 0 && !spendResource('metal', costMetal)) return;
      if (costWood > 0 && !spendResource('wood', costWood)) return;
      if (costLeather > 0 && !spendResource('leather', costLeather)) return;
      
      addWeaponToInventory(weaponId, 1);
    }
  };

  const categoryWeapons = getWeaponsByCategory(activeCategory);

  const WeaponSlot = ({ weapon }: { weapon: Weapon }) => {
    const rankNeeded = getRankNeeded(weapon.id);
    const isUnlocked = maxFighterRank >= rankNeeded;
    
    const costGold = weapon.cost || 0;
    const costMetal = weapon.costMetal || 0;
    const costWood = weapon.costWood || 0;
    const costLeather = weapon.costLeather || 0;
    
    const canAfford = gold >= costGold && 
                      resources.metal >= costMetal && 
                      resources.wood >= costWood && 
                      resources.leather >= costLeather;

    const isAvailable = isUnlocked && canAfford;
    const inventoryCount = weaponInventory[weapon.id] || 0;

    return (
      <div
        className="relative group flex flex-col items-center"
        onMouseEnter={() => setHoveredWeapon(weapon)}
        onMouseLeave={() => setHoveredWeapon(null)}
      >
        <div
          className={`
            w-24 h-24 sm:w-28 sm:h-28 p-1 bg-black/70 border-2 rounded flex flex-col items-center justify-center
            transition-all duration-200
            ${isAvailable 
              ? 'border-yellow-500 hover:border-yellow-300 hover:bg-yellow-900/30 shadow-[0_0_15px_rgba(234,179,8,0.5)]' 
              : 'border-gray-700 opacity-50'
            }
            ${!isUnlocked ? 'grayscale' : ''}
          `}
        >
          <img
            src={getWeaponSprite(weapon)}
            alt={t.getWeaponName(weapon.name)}
            className="w-20 h-20 sm:w-24 sm:h-24 object-contain filter drop-shadow-[0_0_8px_rgba(255,215,0,0.6)]"
            style={{ imageRendering: 'pixelated' }}
            onError={(e) => {
              (e.target as any).style.display = 'none';
              const el = (e.target as any).parentElement;
              if (!el.getAttribute('data-has-emoji')) {
                el.setAttribute('data-has-emoji', 'true');
                el.insertAdjacentHTML('beforeend', `<span class="text-3xl">${weapon.name.includes('shield') ? '🛡️' : weapon.name.includes('armor') ? '👕' : '🗡️'}</span>`);
              }
            }}
          />
          {inventoryCount > 0 && (
            <div className="absolute -top-1 -right-1 bg-yellow-600 text-black text-[8px] font-bold px-1 rounded border border-yellow-400">
              {inventoryCount}
            </div>
          )}
          {!isUnlocked && (
            <div className="absolute bottom-0 left-0 right-0 bg-red-900/90 text-red-200 text-[6px] text-center py-0.5 border-t border-red-700">
              {t.modals.forge.rank} {rankNeeded}
            </div>
          )}
        </div>
        
        {/* Кнопка Купить */}
        <button
          onClick={() => isAvailable && handleBuy(weapon.id)}
          disabled={!isAvailable}
          style={{
            backgroundColor: isAvailable ? '#ca8a04' : '#374151',
            color: isAvailable ? '#000' : '#9ca3af',
            padding: '0.4rem 0.8rem',
            fontSize: '0.6rem',
            fontWeight: 'bold',
            fontFamily: '"Press Start 2P", cursive, sans-serif',
            border: 'none',
            borderRadius: '0.25rem',
            cursor: isAvailable ? 'pointer' : 'not-allowed',
            textTransform: 'uppercase',
            letterSpacing: '0.1em',
            boxShadow: isAvailable ? '0 3px 0 #854d0e' : 'none',
            transition: 'none',
            marginTop: '0.5rem',
            width: '100%',
            maxWidth: '120px'
          }}
          onMouseDown={(e) => {
            if (isAvailable) {
              e.currentTarget.style.transform = 'translateY(1.5px)';
              e.currentTarget.style.boxShadow = '0 1.5px 0 #854d0e';
            }
          }}
          onMouseUp={(e) => {
            if (isAvailable) {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 3px 0 #854d0e';
            }
          }}
        >
          {t.modals.forge.buy}
        </button>
        
      </div>
    );
  };

  const categories: { id: Category; label: string; icon: string }[] = [
    { id: 'melee', label: t.modals.forge.categories.melee, icon: '🗡️' },
    { id: 'shields', label: t.modals.forge.categories.shields, icon: '🛡️' },
    { id: 'armor', label: t.modals.forge.categories.armor, icon: '👕' },
    { id: 'ranged', label: t.modals.forge.categories.ranged, icon: '🏹' },
  ];

  return (
    <BuildingModal
      title={t.modals.forge.title}
      onClose={onClose}
      content={
        <div className="text-white font-pixel text-xs relative">
          {!buildings.forge ? (
            <div className="flex flex-col items-center justify-center py-12">
              <img
                src="./sprites/buildings/forge.png"
                alt="Кузница"
                className="w-24 h-24 mb-6 object-contain filter drop-shadow-[0_0_8px_rgba(255,255,255,0.8)]"
                onError={(e) => {
                  (e.target as any).style.display = 'none';
                  const el = (e.target as any).parentElement;
                  if (!el.getAttribute('data-has-emoji')) {
                    el.setAttribute('data-has-emoji', 'true');
                    el.insertAdjacentHTML('afterbegin', '<div class="text-6xl mb-6">⚒️</div>');
                  }
                }}
              />
              <div className="mb-6 text-gray-400 uppercase tracking-widest text-sm">{t.modals.forge.notBuilt}</div>
              <button 
                onClick={() => buildBuilding('forge')} 
                disabled={resources.metal < 50}
                style={{
                  backgroundColor: resources.metal >= 50 ? '#ca8a04' : '#374151',
                  color: resources.metal >= 50 ? '#000' : '#9ca3af',
                  padding: '0.75rem 1.5rem',
                  fontSize: '0.7rem',
                  fontWeight: 'bold',
                  fontFamily: '"Press Start 2P", cursive, sans-serif',
                  border: 'none',
                  borderRadius: '0.25rem',
                  cursor: resources.metal >= 50 ? 'pointer' : 'not-allowed',
                  textTransform: 'uppercase',
                  letterSpacing: '0.1em',
                  boxShadow: resources.metal >= 50 ? '0 4px 0 #854d0e' : 'none',
                  transition: 'none'
                }}
                onMouseDown={(e) => {
                  if (resources.metal >= 50) {
                    e.currentTarget.style.transform = 'translateY(2px)';
                    e.currentTarget.style.boxShadow = '0 2px 0 #854d0e';
                  }
                }}
                onMouseUp={(e) => {
                  if (resources.metal >= 50) {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 4px 0 #854d0e';
                  }
                }}
              >
                {t.modals.forge.build} {t.modals.forge.buildCost}
              </button>
            </div>
          ) : (
            <div className="flex flex-col relative">
              {/* Фон кузницы с атмосферой */}
              <div 
                className="absolute inset-0 opacity-10 pointer-events-none"
                style={{
                  backgroundImage: 'radial-gradient(circle at 30% 50%, rgba(234, 179, 8, 0.3) 0%, transparent 50%), radial-gradient(circle at 70% 50%, rgba(139, 69, 19, 0.2) 0%, transparent 50%)',
                  backgroundSize: '100% 100%',
                }}
              />
              
              {/* Верхняя зона: Ресурсы */}
              <div className="bg-black/80 border-b-2 border-yellow-600/30 p-3 flex-shrink-0 relative z-10 backdrop-blur-sm" style={{ marginBottom: '16px' }}>
                <div className="grid grid-cols-4 gap-2 text-sm">
                  <div className="flex items-center gap-1">
                    <span className="text-yellow-500">🪙</span>
                    <span className={gold > 0 ? 'text-yellow-500' : 'text-red-500'}>{gold}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <span className="text-gray-300">⚒️</span>
                    <span className={resources.metal > 0 ? 'text-gray-300' : 'text-red-500'}>{resources.metal}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <span className="text-orange-400">🌲</span>
                    <span className={resources.wood > 0 ? 'text-orange-400' : 'text-red-500'}>{resources.wood}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <span className="text-amber-600">🦌</span>
                    <span className={resources.leather > 0 ? 'text-amber-600' : 'text-red-500'}>{resources.leather || 0}</span>
                  </div>
                </div>
              </div>

              {/* Табы категорий */}
              <div className="flex gap-1 flex-shrink-0 overflow-x-auto relative z-10 justify-center" style={{ marginBottom: '16px' }}>
                {categories.map((cat) => (
                  <button
                    key={cat.id}
                    onClick={() => setActiveCategory(cat.id)}
                    className={`
                      px-2 py-1.5 text-[9px] font-bold uppercase tracking-wider border-2 rounded flex-shrink-0
                      transition-all
                      ${activeCategory === cat.id
                        ? 'bg-yellow-600 border-yellow-400 text-black shadow-[0_2px_0_0_rgba(180,130,0,1)]'
                        : 'bg-black/40 border-gray-600 text-gray-400 hover:border-gray-500'
                      }
                    `}
                    style={{
                      fontFamily: '"Press Start 2P", cursive, sans-serif',
                    }}
                  >
                    <span className="mr-1">{cat.icon}</span>
                    {cat.label}
                  </button>
                ))}
              </div>

              {/* Центральная зона: Сетка предметов */}
              <div className="pr-1 relative z-10" style={{ marginBottom: '16px' }}>
                {categoryWeapons.length === 0 ? (
                  <div className="text-center py-8 text-gray-500 text-[10px] uppercase">
                    {t.modals.forge.emptyCategory}
                  </div>
                ) : (
                  <div className="grid grid-cols-3 sm:grid-cols-4 gap-3 sm:gap-4 justify-items-center">
                    {categoryWeapons.map((weapon) => (
                      <WeaponSlot key={weapon.id} weapon={weapon} />
                    ))}
                  </div>
                )}
              </div>

              {/* Нижняя зона: Стационарное окно характеристик */}
              <div className="pt-3 border-t-2 border-yellow-600/30 flex-shrink-0 relative z-10 bg-black/80 backdrop-blur-sm rounded p-3">
                {hoveredWeapon ? (
                  <>
                    <div className="text-yellow-500 text-sm mb-3 font-bold uppercase">
                      {t.getWeaponName(hoveredWeapon.name)}
                    </div>
                    <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs mb-3">
                      {hoveredWeapon.attack > 0 && (
                        <div className="flex justify-between">
                          <span className="text-white/60">{t.modals.forge.attack}</span>
                          <span className="text-red-400 font-bold">{hoveredWeapon.attack}</span>
                        </div>
                      )}
                      {hoveredWeapon.activeDefense > 0 && (
                        <div className="flex justify-between">
                          <span className="text-white/60">{t.modals.forge.defenseActive}</span>
                          <span className="text-green-400 font-bold">{hoveredWeapon.activeDefense}</span>
                        </div>
                      )}
                      {hoveredWeapon.passiveDefense > 0 && (
                        <div className="flex justify-between">
                          <span className="text-white/60">{t.modals.forge.defensePassive}</span>
                          <span className="text-yellow-400 font-bold">{hoveredWeapon.passiveDefense}</span>
                        </div>
                      )}
                      <div className="flex justify-between">
                        <span className="text-white/60">{t.modals.forge.actionPoints}</span>
                        <span className="text-blue-400 font-bold">{hoveredWeapon.actionPoints}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-white/60">{t.modals.forge.range}</span>
                        <span className="text-green-400 font-bold">{hoveredWeapon.range}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-white/60">{t.modals.forge.weight}</span>
                        <span className="text-orange-400 font-bold">{hoveredWeapon.weight}</span>
                      </div>
                    </div>
                    <div className="border-t border-white/10 pt-3 mt-3">
                      <div className="text-white/40 text-xs mb-2">{t.modals.forge.cost}</div>
                      <div className="flex flex-wrap gap-x-3 gap-y-1 text-sm">
                        {(() => {
                          const costGold = hoveredWeapon.cost || 0;
                          const costMetal = hoveredWeapon.costMetal || 0;
                          const costWood = hoveredWeapon.costWood || 0;
                          const costLeather = hoveredWeapon.costLeather || 0;
                          return (
                            <>
                              {costGold > 0 && (
                                <div className={gold >= costGold ? 'text-yellow-500' : 'text-red-500'}>
                                  🪙 {costGold}
                                </div>
                              )}
                              {costMetal > 0 && (
                                <div className={resources.metal >= costMetal ? 'text-gray-300' : 'text-red-500'}>
                                  ⚒️ {costMetal}
                                </div>
                              )}
                              {costWood > 0 && (
                                <div className={resources.wood >= costWood ? 'text-orange-400' : 'text-red-500'}>
                                  🌲 {costWood}
                                </div>
                              )}
                              {costLeather > 0 && (
                                <div className={resources.leather >= costLeather ? 'text-amber-600' : 'text-red-500'}>
                                  🦌 {costLeather}
                                </div>
                              )}
                            </>
                          );
                        })()}
                      </div>
                    </div>
                    <div className="text-sm text-white/60 mt-3">
                      {t.modals.forge.stock} {weaponInventory[hoveredWeapon.id] || 0}
                    </div>
                  </>
                ) : (
                  <div className="text-gray-500 text-sm uppercase text-center">
                    {t.modals.forge.hoverHint}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      }
    />
  );
};
